import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useColorTheme } from '../../context/ColorThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AchievementDashboardSummary = ({ user }) => {
  const { colorTheme } = useColorTheme();
  const [stats, setStats] = useState(null);
  const [recentAchievements, setRecentAchievements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadAchievementSummary();
    }
  }, [user]);

  const loadAchievementSummary = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const response = await axios.get(`${API}/achievements/user`, { headers });
      
      setStats(response.data.user_stats || {});
      setRecentAchievements(response.data.user_stats?.latest_achievements?.slice(0, 3) || []);
      
    } catch (err) {
      console.error('Error loading achievement summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = colorTheme === 'purple' ? {
      milestone: 'text-purple-600',
      creative: 'text-blue-600',
      engagement: 'text-purple-600',
      social: 'text-purple-600',
      streak: 'text-red-600',
    } : colorTheme === 'blue' ? {
      milestone: 'text-sky-600',
      creative: 'text-blue-600',
      engagement: 'text-blue-600',
      social: 'text-blue-600',
      streak: 'text-red-600',
    } : {
      milestone: 'text-purple-600',
      creative: 'text-blue-600',
      engagement: 'text-green-600',
      social: 'text-green-600',
      streak: 'text-red-600',
    };
    return colors[category] || 'text-gray-600';
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const time = new Date(timestamp);
    const diffInDays = Math.floor((now - time) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return time.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-4"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">🏆 Achievements</h3>
        <button className={`text-sm ${colorTheme === 'purple' ? 'text-purple-500 hover:text-purple-600' : colorTheme === 'blue' ? 'text-blue-500 hover:text-blue-600' : 'text-green-500 hover:text-green-600'} font-medium`}>
          View All
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center">
          <div className={`text-2xl font-bold ${colorTheme === 'purple' ? 'text-purple-500' : colorTheme === 'blue' ? 'text-blue-500' : 'text-green-500'}`}>{stats.total_achievements || 0}</div>
          <div className="text-xs text-gray-600 dark:text-gray-400">Unlocked</div>
        </div>
        <div className="text-center">
          <div className={`text-2xl font-bold ${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-green-600'}`}>{stats.total_points || 0}</div>
          <div className="text-xs text-gray-600 dark:text-gray-400">Points</div>
        </div>
        <div className="text-center">
          <div className={`text-2xl font-bold ${colorTheme === 'purple' ? 'text-purple-500' : colorTheme === 'blue' ? 'text-blue-500' : 'text-green-500'}`}>{stats.current_streak || 0}</div>
          <div className="text-xs text-gray-600 dark:text-gray-400">Streak</div>
        </div>
      </div>

      {/* Recent Achievements */}
      {recentAchievements.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Recent Achievements</h4>
          <div className="space-y-3">
            {recentAchievements.map((achievement, index) => (
              <div key={achievement.id || index} className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700">
                <div className={`w-8 h-8 bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-400 to-purple-600' : colorTheme === 'blue' ? 'from-blue-400 to-blue-600' : 'from-green-400 to-green-600'} rounded-full flex items-center justify-center text-white text-sm`}>
                  🏆
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    Achievement Unlocked
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatTimeAgo(achievement.unlocked_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category Progress */}
      {stats.achievements_by_category && Object.keys(stats.achievements_by_category).length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Categories</h4>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(stats.achievements_by_category).slice(0, 4).map(([category, count]) => (
              <div key={category} className="flex items-center justify-between text-xs">
                <span className={`capitalize ${getCategoryColor(category)}`}>{category}</span>
                <span className="font-semibold text-gray-900 dark:text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No achievements state */}
      {stats.total_achievements === 0 && (
        <div className="text-center py-4">
          <div className="text-3xl mb-2">🎯</div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Create your first campaign to start earning achievements!
          </p>
        </div>
      )}
    </div>
  );
};

export default AchievementDashboardSummary;